import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const ZanzibarTour = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">6 DAYS ZANZIBAR TOUR</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          6 Days Zanzibar Tour
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Discover the tropical paradise of Zanzibar with this <strong>6 Days Zanzibar Tour</strong>. Immerse yourself in the island’s pristine beaches, vibrant marine life, and rich cultural heritage. From snorkeling at Prison Island and exploring the famous Spice Tour to enjoying a thrilling Safari Blue Sea Adventure and relaxing at a beach resort, this tour offers a perfect blend of adventure and leisure.
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 6 Days Zanzibar Tour
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival & Relaxation</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Arrive at Zanzibar Airport, where you will be warmly greeted and transferred to your beach resort. Enjoy welcome drinks, refresh, and unwind by the beach, soaking in the serene atmosphere of Zanzibar’s white sand shores. Spend the evening at leisure, preparing for the exciting days ahead.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Resort & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Prison Island, Spice Tour & Sunset Dhow Cruise</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a delicious breakfast, transfer to the sea ferry terminal for a boat trip to Prison Island (Changuu Island). Enjoy snorkeling, swimming, and sunbathing, and explore the island’s historical sites, including the giant tortoises. Return to Stone Town for a Spice Tour, discovering tropical fruits and spices like cloves, cinnamon, and nutmeg. In the evening, embark on a sunset dhow cruise at 4:15 PM, enjoying scenic views before returning to your resort.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Resort & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Safari Blue Sea Adventure</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Start your day with breakfast, then transfer to Fumba Fishing Village for a full-day Safari Blue Sea Adventure. Sail by traditional dhow to Kwale Island, where you’ll enjoy snorkeling, swimming in the Blue Lagoon, and sunbathing on white sandbars. Savor a delicious seafood and barbecue lunch, connect with fellow travelers, and relax before returning to your resort in the late evening.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Resort & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Days 4-5: Leisure at the Beach Resort</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Enjoy two leisurely days at your beach resort after breakfast. Relax by the pool or beach, or opt for activities like swimming with turtles, snorkeling with dolphins, or kayaking (available upon request). These days offer flexibility to unwind or explore at your own pace, with delicious dinners included.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Paje Beach Resort & Spa or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 6: Departure from Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a final breakfast, transfer to Stone Town for a last shopping experience at the bazaar and souvenir shops on Mizingani Street, exploring Zanzibar’s charming narrow streets. Afterward, transfer to Zanzibar Airport for your scheduled flight home.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Airport transfers in Zanzibar
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals as stated (half board)
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Prison Island trip, Spice Tour, and Safari Blue Sea Adventure
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Sunset dhow cruise
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International flights and visa fees
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities (e.g., swimming with turtles, kayaking)
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Alcoholic beverages
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">6 Days Zanzibar Tour</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 6 Days, 5 Nights
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Zanzibar
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Airport pickup - Return to airport
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZanzibarTour;